/**
 * Shared Document Formatting Configuration
 *
 * Single source of truth for all document rendering — on-screen previews,
 * jsPDF exports, and HTML templates. Keeps formatting consistent across
 * all document types.
 *
 * Mirrors backend/src/constants/documentConfig.js where applicable.
 */

import { brand, text, bg, border, status } from '../../theme/colors';

// ── Legal Entity ────────────────────────────────────────────────────────────
export const LEGAL_ENTITY = 'Eqaya (Private) Limited';
export const LEGAL_JURISDICTION = 'Governed by the laws of Zimbabwe';
export const LEGAL_ACT = 'Electronic Transactions Act [Chapter 22:05] of Zimbabwe';

// ── Contact ─────────────────────────────────────────────────────────────────
export const CONTACT = {
  email: 'info@eqaya.com',
  support: 'info@eqaya.com',
  website: 'www.eqaya.com',
  tagline: "Africa's trusted construction marketplace",
};

// ── Brand Colors (RGB arrays for jsPDF) ─────────────────────────────────────
export const PDF_COLORS = {
  brand:         [13, 148, 136],   // #0d9488
  brandDark:     [15, 118, 110],   // #0f766e
  brandLight:    [240, 253, 250],  // #f0fdfa
  textPrimary:   [30, 41, 59],     // #1e293b
  textSecondary: [100, 116, 139],  // #64748b
  white:         [255, 255, 255],
  black:         [0, 0, 0],
  // Status colors
  success:       [22, 163, 74],    // #16a34a
  warning:       [217, 119, 6],    // #d97706
  error:         [220, 38, 38],    // #dc2626
  info:          [37, 99, 235],    // #2563eb
  // Table
  tableStripe:   [248, 250, 251],  // #F8FAFB
  tableBorder:   [226, 232, 240],  // #E2E8F0
};

// ── Hex Colors (for HTML templates) ─────────────────────────────────────────
export const HEX_COLORS = {
  brand:         brand.primary,     // #0d9488
  brandDark:     brand.primaryDark, // #0f766e
  brandDeep:     brand.primaryDeep, // #134e4a
  brandLight:    '#f0fdfa',
  textPrimary:   text.primary,      // #1e293b
  textSecondary: text.secondary,    // #64748b
  textTertiary:  text.tertiary,     // #94a3b8
  bgPage:        bg.page,           // #f8fafc
  bgPaper:       bg.paper,          // #ffffff
  bgSubtle:      bg.subtle,         // #f1f5f9
  borderLight:   border.light,      // #e2e8f0
  borderDefault: border.default,    // #cbd5e1
  success:       status.success,    // #16a34a
  warning:       status.warning,    // #d97706
  error:         status.error,      // #dc2626
  info:          status.info,       // #2563eb
};

// ── Standardized Font Sizes (pt for PDF, px for HTML) ───────────────────────
export const FONT_SIZES = {
  // PDF (jsPDF uses pt)
  pdf: {
    coverTitle:    22,
    documentTitle: 16,
    sectionHeader: 13,
    subsectionHeader: 11,
    body:          10,
    bodySmall:     9,
    caption:       8,
    footer:        7,
    table: {
      header:      9,
      body:        9,
    },
  },
  // HTML (px)
  html: {
    documentTitle: '28px',
    sectionHeader: '22px',
    subsectionHeader: '16px',
    body:          '15px',
    bodySmall:     '14px',
    caption:       '13px',
    footer:        '12px',
  },
};

// ── Table Styling ───────────────────────────────────────────────────────────
export const TABLE_STYLE = {
  headerBg:      PDF_COLORS.brand,
  headerText:    PDF_COLORS.white,
  stripeBg:      PDF_COLORS.tableStripe,
  borderColor:   PDF_COLORS.tableBorder,
  cellPadding:   3,  // mm for jsPDF
  fontSize:      FONT_SIZES.pdf.table.body,
};

// ── Header Configuration ────────────────────────────────────────────────────
export const HEADER = {
  height:        35,  // mm for jsPDF
  bgColor:       PDF_COLORS.brand,
  textColor:     PDF_COLORS.white,
  titleSize:     FONT_SIZES.pdf.documentTitle,
};

// ── Footer Configuration ────────────────────────────────────────────────────
export const FOOTER = {
  text: LEGAL_ENTITY,
  tagline: CONTACT.tagline,
  contact: `${CONTACT.email} | ${CONTACT.website}`,
  jurisdiction: LEGAL_JURISDICTION,
  fontSize: FONT_SIZES.pdf.footer,
  textColor: PDF_COLORS.textSecondary,
};

// ── Page Configuration ──────────────────────────────────────────────────────
export const PAGE = {
  margin:        15,  // mm
  contentWidth:  180, // mm (A4 = 210 - 2*15)
};

// ── Currency Formatting ─────────────────────────────────────────────────────
export const CURRENCIES = {
  USD: { symbol: '$', name: 'United States Dollar' },
  ZWG: { symbol: 'ZiG', name: 'Zimbabwe Gold' },
  ZAR: { symbol: 'R', name: 'South African Rand' },
  BWP: { symbol: 'P', name: 'Botswana Pula' },
};

export function formatCurrency(amount, currencyCode = 'USD') {
  const currency = CURRENCIES[currencyCode] || CURRENCIES.USD;
  const num = parseFloat(amount);
  if (isNaN(num)) return `${currency.symbol}0.00`;
  return `${currency.symbol}${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
